import { useCallback } from 'react';
import { assignRandomId } from '../common/util';
import { entriesAddMany } from '../containers/contentSlice';
import { useAppDispatch } from '../hooks';
import { useCreateReferencesMutation } from '../services/references';
import { repositoryEntriesApi } from '../services/repositoryEntries';
import {
	AnchorEntry,
	CanvasPosition,
	EntityType,
	EntryProperty,
	EntryPropertyBelongsTo,
	ReferenceEntry,
	RepositoryEntry,
} from '../types';
import {
	computePrimaryAxisReferencePositions,
	sortedRepositoryEntryIdsForAxisRepository,
} from '../utils/canvasGridUtils';

type UsePopulateAnchorReferencesArgs = {
	allRepositoryEntries: RepositoryEntry[];
	allEntryProperties: EntryProperty[];
	parentId: string | null;
	isAuthenticated: boolean;
};

export default function usePopulateAnchorReferences({
	allRepositoryEntries,
	allEntryProperties,
	parentId,
	isAuthenticated,
}: UsePopulateAnchorReferencesArgs) {
	const dispatch = useAppDispatch();
	const [referenceCreateMutation] = useCreateReferencesMutation();

	const populateAnchorReferences = useCallback(
		async (
			anchor: AnchorEntry,
			anchorPosition: CanvasPosition,
			filter?: { slug: string; targetEntryId: string },
		): Promise<ReferenceEntry[]> => {
			if (!anchor.repositoryId) {
				return [];
			}

			const axisRepositoryId = anchor.repositoryId;

			let repositoryEntryIds: string[] = [];
			if (isAuthenticated) {
				const dataFilter = filter
					? JSON.stringify({ [filter.slug]: { targetEntryId: filter.targetEntryId } })
					: undefined;
				const response = await dispatch(
					repositoryEntriesApi.endpoints.getRepositoryEntriesByFilter.initiate(
						{ repositoryId: axisRepositoryId, includeArchived: false, dataFilter },
						{ forceRefetch: Boolean(filter) },
					),
				).unwrap();
				repositoryEntryIds = (response.rows ?? [])
					.filter((row) => {
						if (row.isArchived) return false;
						if (!filter) return true;
						const propData = row.data[filter.slug];
						return Array.isArray(propData);
					})
					.sort((a, b) =>
						String(a.rank ?? '').localeCompare(String(b.rank ?? ''), undefined, { numeric: true }),
					)
					.map((row) => row.id);
			} else if (!filter) {
				repositoryEntryIds = sortedRepositoryEntryIdsForAxisRepository(
					axisRepositoryId,
					allRepositoryEntries,
				);
			} else {
				const matchingEntryIds = new Set(
					allEntryProperties
						.filter((p): p is EntryPropertyBelongsTo =>
							p.type === EntityType.EntryPropertyBelongsTo
							&& p.slug === filter.slug
							&& p.value === filter.targetEntryId,
						)
						.map((p) => p.parentId),
				);
				repositoryEntryIds = allRepositoryEntries
					.filter(
						(entry) =>
							entry.repositoryId === axisRepositoryId
							&& entry.parentId === axisRepositoryId
							&& matchingEntryIds.has(entry.id),
					)
					.sort((a, b) =>
						String(a.rank ?? '').localeCompare(String(b.rank ?? ''), undefined, { numeric: true }),
					)
					.map((entry) => entry.id);
			}

			if (!repositoryEntryIds.length) {
				return [];
			}

			// Vertical anchor projects downward → place entries along Y axis (primaryLayoutVertical = false).
			// Horizontal anchor projects rightward → place entries along X axis (primaryLayoutVertical = true).
			const primaryLayoutVertical = (anchor.direction ?? 'vertical') === 'horizontal';
			const positions = computePrimaryAxisReferencePositions({
				count: repositoryEntryIds.length,
				gridX: anchorPosition.x,
				gridY: anchorPosition.y,
				primaryLayoutVertical,
			});

			const payloads = repositoryEntryIds.map((repositoryEntryId, index) =>
				assignRandomId({
					type: EntityType.Reference,
					parentId,
					repositoryId: axisRepositoryId,
					repositoryEntryId,
					xy: positions[index],
					anchorId: anchor.id,
				}) as ReferenceEntry,
			);

			if (isAuthenticated) {
				const created = await referenceCreateMutation(payloads).unwrap();
				return created as ReferenceEntry[];
			}
			dispatch(entriesAddMany(payloads));
			return payloads;
		},
		[allEntryProperties, allRepositoryEntries, dispatch, isAuthenticated, parentId, referenceCreateMutation],
	);

	return { populateAnchorReferences };
}
